(() => {
  if (customElements.get('product-delivery-delm')) return;
  // ISO 3166-1 country codes; names are supplied by the browser's locale data.
  const countryCodes = 'AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BV BW BY BZ CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO FR GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW GY HK HM HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE JM JO JP KE KG KH KI KM KN KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT MU MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS PT PW PY QA RE RO RS RU RW SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TF TG TH TJ TK TL TM TN TO TR TT TV TW TZ UA UG UM US UY UZ VA VC VE VG VI VN VU WF WS YE YT ZA ZM ZW'.split(' ');
  let activeCountry;
  const storageKey = 'delm-delivery-country';
  const normalize = (value) => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase();

  customElements.define('product-delivery-delm', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;
      const productInfo = this.closest('product-info');
      if (!productInfo) return;
      this.bindCountryPicker(signal);
      this.bindBreakdown(signal);

      // Teeinblue replaces Dawn's price with its own asynchronously mounted form.
      // Keep the same widget immediately after that visible price, without cloning it.
      const placeBelowPrice = () => {
        if (signal.aborted || !this.isConnected) return;
        const price = productInfo.querySelector('.tee-product-price');
        if (!price || price.contains(this) || this.contains(price) || price.nextElementSibling === this) return;
        price.after(this);
      };
      this.positionObserver?.disconnect();
      this.positionObserver = new MutationObserver(placeBelowPrice);
      this.positionObserver.observe(productInfo, { childList: true, subtree: true });
      // Moving a custom element reconnects it; defer placement until connection completes.
      queueMicrotask(placeBelowPrice);

      const syncVariant = () => {
        const widget = this.querySelector('delm-widget');
        const variantId = document.getElementById(this.dataset.formId)?.querySelector('[name="id"]')?.value;
        if (!variantId || !widget || widget.dataset.variantId === variantId) return;
        if (typeof widget.setVariantId === 'function' && typeof widget.load === 'function') {
          widget.setVariantId(variantId);
          widget.load();
        } else {
          // Keep the initial attributes current while Delm Core is still loading.
          widget.dataset.variantId = variantId;
        }
      };

      productInfo.addEventListener('change', (event) => {
        if (event.target.name === 'id' && event.target.form?.id === this.dataset.formId) syncVariant();
      }, { signal });
      syncVariant();
      customElements.whenDefined('delm-widget').then(() => {
        if (!signal.aborted) {
          syncVariant();
          this.bindDeliveryText();
        }
      });
    }

    bindDeliveryText() {
      const root = this.querySelector('delm-widget')?.shadowRoot;
      if (!root) return;
      const format = () => {
        if (root.querySelector('[data-delivery-line]')) return;
        // Preserve the dates calculated by Delm; adapt this store's English starter template.
        const heading = root.querySelector('strong')?.textContent.trim();
        const range = heading?.match(/^Delivery between (.+?) and (.+)$/);
        if (!range) return;
        const style = document.createElement('style');
        style.textContent = ':host { display: block; } p { margin: 0; font: inherit; font-size: 1.6rem; line-height: 1.5; } button { padding: 0; border: 0; border-bottom: 2px dotted currentColor; background: transparent; font: inherit; font-weight: 700; color: #102440; cursor: pointer; } button:focus-visible { outline: 2px solid #2860ff; outline-offset: 3px; }';
        const line = document.createElement('p');
        line.dataset.deliveryLine = '';
        line.setAttribute('aria-live', 'polite');
        const dates = document.createElement('button');
        dates.type = 'button';
        dates.setAttribute('aria-haspopup', 'dialog');
        dates.setAttribute('aria-label', `Delivery breakdown: ${range[1]} - ${range[2]}`);
        dates.textContent = `${range[1]} - ${range[2]}`;
        this.arrivalRange = dates.textContent;
        this.querySelector('[data-arrival-date]').textContent = this.arrivalRange;
        dates.addEventListener('click', () => {
          this.breakdownOpener = dates;
          this.querySelector('[data-order-date]').textContent = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(new Date());
          this.querySelector('.delivery-breakdown').showModal();
        });
        line.append(document.createTextNode('Order today and get it by: '), dates);
        root.replaceChildren(style, line);
      };
      this.deliveryTextObserver?.disconnect();
      this.deliveryTextObserver = new MutationObserver(format);
      this.deliveryTextObserver.observe(root, { childList: true, subtree: true });
      format();
    }

    bindBreakdown(signal) {
      const dialog = this.querySelector('.delivery-breakdown');
      if (!dialog) return;
      dialog.querySelectorAll('[data-breakdown-close]').forEach((button) => {
        button.addEventListener('click', () => dialog.close(), { signal });
      });
      dialog.addEventListener('close', () => {
        const opener = this.querySelector('delm-widget')?.shadowRoot?.querySelector('[data-delivery-line] button');
        opener?.focus();
      }, { signal });
      dialog.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          dialog.close();
        }
      }, { signal });
      dialog.addEventListener('click', (event) => {
        if (event.target !== dialog) return;
        const r = dialog.getBoundingClientRect();
        if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) dialog.close();
      }, { signal });
    }

    bindCountryPicker(signal) {
      const dialog = this.querySelector('.delivery-country-dialog');
      if (!dialog) return;
      const opener = this.querySelector('[data-country-open]');
      const search = this.querySelector('[data-country-search]');
      if (!this.countriesBuilt && typeof Intl.DisplayNames === 'function') {
        const list = this.querySelector('.delivery-country-dialog__list');
        const names = new Intl.DisplayNames(['en'], { type: 'region' });
        const existing = new Set([...list.querySelectorAll('[data-country-code]')].map((option) => option.dataset.countryCode));
        countryCodes.forEach((code) => {
          if (existing.has(code)) return;
          const option = document.createElement('button');
          option.type = 'button';
          option.className = 'delivery-country-dialog__option';
          option.dataset.countryCode = code;
          option.dataset.countryName = names.of(code);
          const flag = document.createElement('span');
          flag.className = 'delivery-country-dialog__flag';
          flag.setAttribute('aria-hidden', 'true');
          const label = document.createElement('span');
          label.textContent = names.of(code);
          option.append(flag, label);
          list.append(option);
        });
        const sorted = [...list.querySelectorAll('[data-country-code]')].sort((a, b) => a.dataset.countryName.localeCompare(b.dataset.countryName, 'en'));
        list.append(...sorted, this.querySelector('[data-country-empty]'));
        this.countriesBuilt = true;
      }
      const options = [...this.querySelectorAll('[data-country-code]')];
      if (!options.length) return;
      const validCountry = (code) => options.some((option) => option.dataset.countryCode === code);
      const showCountry = (code) => {
        const selected = options.find((option) => option.dataset.countryCode === code);
        if (!selected) return;
        opener.textContent = selected.dataset.countryName;
        options.forEach((option) => option.setAttribute('aria-pressed', String(option === selected)));
      };
      const setCountry = (code) => {
        if (!validCountry(code)) return;
        showCountry(code);
        if (activeCountry === code) return;
        activeCountry = code;
        try { localStorage.setItem(storageKey, code); } catch { /* Storage is optional. */ }
        // Delm's command queue works before and after Delm Core loads.
        window.$delm = window.$delm || [];
        window.$delm.push(['setShippingAddress', { countryCode: code }]);
        window.$delm.push(['loadAllWidgets']);
        window.dispatchEvent(new CustomEvent('delm:destination-change', { detail: code }));
      };
      const filter = () => {
        const query = normalize(search.value.trim());
        let count = 0;
        options.forEach((option) => {
          option.hidden = !normalize(`${option.dataset.countryName} ${option.dataset.countryCode}`).includes(query);
          if (!option.hidden) count++;
        });
        this.querySelector('[data-country-empty]').hidden = count > 0;
        this.querySelector('[data-country-status]').textContent = `${count} countries found`;
      };
      options.forEach((option) => {
        const code = option.dataset.countryCode;
        option.querySelector('.delivery-country-dialog__flag').textContent = /^[A-Z]{2}$/.test(code)
          ? String.fromCodePoint(...[...code].map((letter) => letter.charCodeAt(0) + 127397)) : '';
        option.addEventListener('click', () => {
          setCountry(code);
          dialog.close();
        }, { signal });
      });
      opener.addEventListener('click', () => {
        search.value = '';
        filter();
        dialog.showModal();
        search.focus();
        this.querySelector('.delivery-country-dialog__list').scrollTop = 0;
      }, { signal });
      this.querySelector('[data-country-close]').addEventListener('click', () => dialog.close(), { signal });
      dialog.addEventListener('close', () => opener.focus(), { signal });
      dialog.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          dialog.close();
        }
      }, { signal });
      dialog.addEventListener('click', (event) => {
        if (event.target !== dialog) return;
        const rect = dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
      }, { signal });
      // This popup may live inside Teeinblue's form; Enter must never submit an order.
      search.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') event.preventDefault();
      }, { signal });
      search.addEventListener('input', filter, { signal });
      window.addEventListener('delm:destination-change', (event) => showCountry(event.detail), { signal });
      window.addEventListener('storage', (event) => {
        if (event.key === storageKey && validCountry(event.newValue)) setCountry(event.newValue);
      }, { signal });
      let country = activeCountry;
      try { country = country || localStorage.getItem(storageKey); } catch { /* Storage is optional. */ }
      if (!validCountry(country)) country = this.dataset.country;
      if (!validCountry(country)) country = options[0].dataset.countryCode;
      setCountry(country);
      this.querySelector('[data-country-heading]').hidden = false;
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.positionObserver?.disconnect();
      this.deliveryTextObserver?.disconnect();
    }
  });
})();
